<script lang="ts">
	import { Alert, Button, Text, Modal, Container, Icon } from '@Shelly/shelly-design-system';
	import InvoiceDetailsModal from './InvoiceDetailsModal.svelte';
	import { getContext, onMount } from 'svelte';
	import { COUNTRIES } from 'src/constants';
	import { paypal, paypalTest } from 'src/services/http';
	import { format } from 'svelte-i18n';
	import { getCurrentUnitPremiumInfo } from 'src/helpers/paypal_info_helpers';
	import { SubscriptionStatus, type PaypalInfoResponse } from 'src/types/Subscription';
	import { capFirstChar } from 'src/helpers/utilities';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	let showModal = false;
	let showConfirmModal = false;
	let isPaypalStatusActive;

	let invoiceDetails: SvelteStore<any> = getContext('invoice_details');

	$: country = COUNTRIES.find((country) => {
		if ($invoiceDetails.country === 'GR') {
			return country.CountryCode === 'EL';
		}
		return country.CountryCode === $invoiceDetails.country;
	});

	const deleteConfirmation = () => {
		showConfirmModal = true;
	};

	const onCloseConfirmModal = () => {
		showConfirmModal = false;
	};

	const deleteInvoiceDetails = async () => {
		try {
			let res;
			if (isDevServerUser(['repo', 't1', 'nb'])) {
				res = await paypalTest.delete('/api/invoice_data');
			} else {
				res = await paypal.get('/api/invoice_data');
			}
			$invoiceDetails = {};
		} catch (error) {}
	};

	onMount(async () => {
		const paypalStatusResponse = (await getCurrentUnitPremiumInfo()) as PaypalInfoResponse;

		isPaypalStatusActive =
			paypalStatusResponse.subscription_status === SubscriptionStatus.Active ? true : false;
	});
</script>

{#if !$invoiceDetails?.type}
	<Alert
		class="mb-s"
		heading={$format('alert_premium_no_invoice_details', {
			default: 'No invoice details provided. Click the button below to add details.',
		})}
	/>
{:else}
	<div class="d-flex mb-s">
		<Text class="mr-s text-bold" styleType="controls-small">
			{$format('text_premium_invoice_receiver_type', {
				default: 'Invoice Receiver Type',
			})}
		</Text>

		<Text styleType="body-small">
			{capFirstChar(
				$format(`label_invoice_details_${$invoiceDetails?.type}`, {
					default: $invoiceDetails?.type,
				}),
			)}</Text
		>
	</div>
	<div class="d-flex mb-s">
		<Text class="mr-s text-bold" styleType="controls-small"
			>{$format('text_invoice_details_country', { default: 'Country' })}
		</Text>
		<Text styleType="body-small">{country?.CountryName}</Text>
	</div>
	{#if $invoiceDetails?.type === 'business'}
		<div class="d-flex mb-s">
			<Text class="mr-s text-bold" styleType="controls-small"
				>{$format('invoice_vat', { default: 'VAT' })}</Text
			>
			<Text styleType="body-small">{$invoiceDetails?.vat}</Text>
		</div>
		<div class="d-flex mb-s">
			<Text class="mr-s text-bold" styleType="controls-small"
				>{$format('invoice_company_name', { default: 'Company Name' })}
			</Text>
			<Text styleType="body-small">{$invoiceDetails?.name}</Text>
		</div>
		<div class="d-flex mb-s">
			<Text class="mr-s text-bold" styleType="controls-small"
				>{$format('invoice_address', { default: 'Address' })}
			</Text>
			<Text styleType="body-small">{$invoiceDetails?.address}</Text>
		</div>
	{/if}
{/if}

<Button
	size="m"
	on:click={() => (showModal = true)}
	class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mt-sm-s mt-md-0"
>
	{$invoiceDetails?.type
		? $format('invoice_edit_details', { default: 'Edit' })
		: $format('invoice_add_details', { default: 'Add' })}
</Button>
<Button
	disabled={!$invoiceDetails?.type}
	size="m"
	variant="danger"
	on:click={deleteConfirmation}
	class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mt-sm-s mt-md-0"
>
	{$format('invoice_delete_details', { default: 'Delete Details' })}
</Button>

{#if showModal}
	<InvoiceDetailsModal bind:showModal />
{/if}

<Modal
	on:close={onCloseConfirmModal}
	showModal={showConfirmModal}
	title={`${$format('button_delete', {
		default: 'Delete',
	})} ${$format('invoice_details')}?`}
>
	<Container slot="content" padding="0">
		<div class="d-flex flex-direction-column align-items-center">
			<Icon size="xxl" icon="fa-light fa-trash" class="text-danger mb-xs" />
			<Text class="width-full">
				{isPaypalStatusActive
					? $format('deleting_invoice_details_msg', {
							default:
								'Please note that deleting invoice details requires canceling your premium subscription first.',
						})
					: $format('on_delete_invoice_details', {
							default: `Deleting invoice details will permanently remove them from your account.`,
						})}
			</Text>
		</div>
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={onCloseConfirmModal}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			disabled={isPaypalStatusActive}
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			variant="danger"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				deleteInvoiceDetails();
				showConfirmModal = false;
			}}
		>
			{$format('button_delete', { default: 'Delete' })}
			{$format('details_step').toLowerCase()}
		</Button>
	</div>
</Modal>
